import type { Scale } from '@tunasong/schemas'
import { concertKeyOf } from '../chord/concert-pitch.js'
import { fitIntoRange } from './transpose-into-range.js'
import type { VocalRange } from './types.js'

/**
 * Where a Pitch Trainer phrase is sung (Phase 2, plan M3; Bowie §2):
 * - `as-written`: no calibrated range, or a chord line, which is built inside the range already;
 * - `octave`: moved by whole octaves (0 when it already fits);
 * - `key-change`: no octave fits, so the smallest key change that does, ties going down. With a known
 *   key, both keys are named for "Moved from {{songKey}} to {{key}}". The caller re-spells the notes by
 *   extracting the moved melody in `to`;
 * - `refused`: wider than the range, with the numbers for `vocal-phrase-out-of-range`.
 */
export type PhraseFit =
  | { kind: 'as-written'; midis: number[] }
  | { kind: 'octave'; octaves: number; midis: number[] }
  | { kind: 'key-change'; semitones: number; keyChange: number; midis: number[]; from?: Scale; to?: Scale }
  | { kind: 'refused'; spanSemitones: number; rangeSemitones: number }

export interface FitPhraseOptions {
  /** Null for a singer who has not measured their range */
  range: VocalRange | null
  /** The phrase's key, for naming the key it moves to */
  key?: Scale
  isChordLine?: boolean
}

export const fitPhrase = (midis: number[], { range, key, isChordLine = false }: FitPhraseOptions): PhraseFit => {
  if (!range || isChordLine || midis.length === 0) {
    return { kind: 'as-written', midis }
  }
  const spanSemitones = Math.max(...midis) - Math.min(...midis)
  const rangeSemitones = range.high - range.low
  if (spanSemitones > rangeSemitones) {
    return { kind: 'refused', spanSemitones, rangeSemitones }
  }
  const fit = fitIntoRange(midis, range, { keyChange: true })
  if (!fit.fits) {
    return { kind: 'refused', spanSemitones, rangeSemitones }
  }
  if (fit.keyChange === 0) {
    return { kind: 'octave', octaves: fit.semitones / 12, midis: fit.midis }
  }
  const to = concertKeyOf(key, fit.semitones)
  return {
    kind: 'key-change',
    semitones: fit.semitones,
    /** −6 to +6: the key change within `semitones`, which the original-key verdict compares against */
    keyChange: fit.keyChange,
    midis: fit.midis,
    ...(key && to ? { from: key, to } : {}),
  }
}
